import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { NavigationEnd, Router } from "@angular/router";
import {
  concat,
  Observable,
  of,
  Subject,
  Subscription,
  throwError
} from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap
} from "rxjs/operators";
import { PersonType } from "../../enum/person-type.enum";
import { Counterpart } from "../../model/domain/Counterpart";
import { AuthService } from "../../services/auth.service";
import { BankService } from "../../services/bank.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { Roles } from "../auth/auth.role";
import { CounterpartAccount } from "./../../model/domain/CounterpartAccount";
import { TicketType } from "./../../model/domain/Entity";

@Component({
  selector: "app-counterpart",
  templateUrl: "./counterpart.component.html",
  styleUrls: ["./counterpart.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CounterpartComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private service: TicketService,
    private router: Router,
    public validator: ValidatorService,
    private authService: AuthService,
    private bankService: BankService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isValidating) {
          this.resetForm();
        }
      }
    });
  }

  @ViewChild("thisForm", { static: true }) counterpartForm: NgForm;

  @Input() counterpartModel: Counterpart = new Counterpart();
  @Input() ticketType: TicketType = null;
  @Input() canEdit = false;
  @Input() isNoFinancialQuota: boolean;
  @Input() isTedQuota: boolean;
  @Input() isCetipQuota: boolean;
  @Input() isSecondaryMarketQuota: boolean;
  @Input() showHeader;
  @Input() isTedCETIP: boolean;
  @Input() isCETIP: boolean;
  @Input() isNoFinancialCETIP: boolean;
  @Input() isSecondaryMarketCetip: boolean;
  @Input() isRFPrivateMargin: boolean;
  @Input() isRFPublicMargin: boolean;
  @Input() isVariableIncomeMargin: boolean;
  @Input() isMoneyMargin: boolean;
  @Input() canValidate = false;
  @Input() model;
  @Input() isStructuredOrder = false;
  @Input() isStructuredOrderNoFinancial = false;

  @Output() emitterSelectedCounterpart: EventEmitter<any> = new EventEmitter();
  @Output() emitterSelectedCounterpartAccount: EventEmitter<any> =
    new EventEmitter();

  isValidating = false;
  formEnabled = true;
  navigationSubscription: Subscription;

  counterpartList: Observable<Counterpart[]>;
  counterpartSearchResult: Counterpart[] = null;
  counterpartSearchTerm = new Subject<string>();

  counterpartByName: Observable<Counterpart[]>;
  counterpartByDocument: Observable<Counterpart[]>;
  counterpartSearchNameTerm = new Subject<string>();
  counterpartSearchDocumentTerm = new Subject<string>();

  selectedCounterpart: Counterpart = new Counterpart();
  counterpartAccountList: CounterpartAccount[] = null;
  selectedCounterpartAccount: CounterpartAccount = new CounterpartAccount();

  isNewCounterpart: boolean;

  ngOnInit() {
    this.searchCounterpart();
  }

  ngOnChanges() {
    this.setEnable(this.canEdit);
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  edit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateExpensesTicket)
    );
  }

  getErrors(): string[] {
    const validationErrors = [];
    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.counterpartModel[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  searchCounterpart() {
    this.counterpartByName = concat(
      of([]),
      this.counterpartSearchNameTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((name) =>
          this.service.getCounterpart(name, null).pipe(catchError(() => of([])))
        )
      )
    );

    this.counterpartByDocument = concat(
      of([]),
      this.counterpartSearchDocumentTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((document) =>
          this.service
            .getCounterpart(null, document)
            .pipe(catchError(() => of([])))
        )
      )
    );
  }

  isQuota(): boolean {
    return this.ticketType === TicketType.Quota;
  }

  isCetip(): boolean {
    return this.ticketType === TicketType.CETIP;
  }

  isSelic(): boolean {
    return this.ticketType === TicketType.SELIC;
  }

  isMargin(): boolean {
    return this.ticketType === TicketType.Margin;
  }

  isContracted(): boolean {
    return this.ticketType === TicketType.Contracted;
  }

  isCurrencyTerm(): boolean {
    return this.ticketType === TicketType.CurrencyTerm;
  }

  isSwapCetip(): boolean {
    return this.ticketType === TicketType.SwapCetip;
  }

  fillComponentWhenCounterpartSelected() {
    this.counterpartModel.name = "";
    this.counterpartModel.document = "";
    this.counterpartModel.clearingAccount = "";
    this.counterpartModel.brokerAccount = "";
    this.counterpartModel.type = 0;

    this.counterpartModel.counterpartAccounts = null;

    this.selectedCounterpartAccount = new CounterpartAccount();

    if (this.selectedCounterpart) {
      if (this.selectedCounterpart.document.length > 11) {
        this.selectedCounterpart.document = this.validator.formatDocument(
          this.selectedCounterpart.document
        );
        this.counterpartModel.type = PersonType.LegalPerson;
      } else {
        this.selectedCounterpart.document =
          this.validator.formatPhysicalDocument(
            this.selectedCounterpart.document
          );
        this.counterpartModel.type = PersonType.PhysicalPerson;
      }

      this.counterpartModel.name = this.selectedCounterpart.name;
      this.counterpartModel.document = this.selectedCounterpart.document;

      if (this.isQuota()) {
        this.counterpartModel.brokerAccount = "";
        this.counterpartAccountList =
          this.selectedCounterpart.counterpartAccounts;
      }

      if (this.isCetip()) {
        this.counterpartAccountList =
          this.selectedCounterpart.counterpartAccounts;
      }

      if (this.isSelic()) {
        this.counterpartAccountList =
          this.selectedCounterpart.counterpartAccounts;
        this.counterpartModel.selicAccount =
          this.selectedCounterpart.selicAccount;
      }

      if (this.isMargin() && !this.isMoneyMargin) {
        if (this.isRFPrivateMargin) {
          this.counterpartModel.clearingAccount =
            this.selectedCounterpart.cetipAccount;
        }

        if (this.isRFPublicMargin) {
          this.counterpartModel.clearingAccount =
            this.selectedCounterpart.selicAccount;
        }

        if (this.isVariableIncomeMargin) {
          this.counterpartModel.clearingAccount =
            this.selectedCounterpart.b3Account;
        }

        this.counterpartModel.brokerAccount =
          this.selectedCounterpart.brokerAccount;

        this.counterpartAccountList =
          this.selectedCounterpart.counterpartAccounts;
      }

      if (this.isContracted()) {
        this.counterpartModel.selicAccount =
          this.selectedCounterpart.selicAccount;
      }

      if (this.isCurrencyTerm()) {
        this.counterpartModel.clearingAccount =
          this.selectedCounterpart.cetipAccount;
      }

      if (this.isSwapCetip()) {
        this.counterpartModel.clearingAccount =
          this.selectedCounterpart.cetipAccount;
      }
    }
  }

  public fillCounterpartData(data: any, counterpartModel: Counterpart): any {
    //enviando campos do component para a model
    if (counterpartModel.name) data.counterpartName = counterpartModel.name;

    data.isNewCounterpart = counterpartModel.isNewCounterpart;

    if (counterpartModel.document != null) {
      data.counterpartDocument = this.validator.sanitize(
        counterpartModel.document
      );
      if (data.counterpartType) {
        data.counterpartType = counterpartModel.type;
      }
    }

    if (this.ticketType === TicketType.Quota) {
      if (
        this.selectedCounterpartAccount.bank &&
        this.selectedCounterpartAccount.branch &&
        this.selectedCounterpartAccount.account
      ) {
        data.fundBank = this.selectedCounterpartAccount.bank;
        data.fundBranch = this.selectedCounterpartAccount.branch;
        data.fundAccount = this.selectedCounterpartAccount.account;
      }
    }

    if (this.ticketType === TicketType.CETIP && !this.isNoFinancialCETIP) {
      if (
        this.selectedCounterpartAccount.bank &&
        this.selectedCounterpartAccount.branch &&
        this.selectedCounterpartAccount.account
      ) {
        data.counterpartBank = this.selectedCounterpartAccount.bank;
        data.counterpartBranch = this.selectedCounterpartAccount.branch;
        data.counterpartAccount = this.selectedCounterpartAccount.account;
      }
    }

    if (this.ticketType === TicketType.SELIC) {
      if (counterpartModel.selicAccount) {
        data.counterpartSelicAccount = counterpartModel.selicAccount;
      }
    }

    if (this.ticketType === TicketType.Margin) {
      if (!this.isMoneyMargin) {
        data.counterpartClearingAccount = counterpartModel.clearingAccount;

        if (counterpartModel.brokerAccount) {
          data.counterpartBrokerAccount = counterpartModel.brokerAccount;
        }
      } else {
        data.bank = this.selectedCounterpartAccount.bank;
        data.branch = this.selectedCounterpartAccount.branch;
        data.account = this.selectedCounterpartAccount.account;
      }

      if (this.isVariableIncomeMargin) {
        data.counterpartBrokerAccount = counterpartModel.brokerAccount;
      }
    }

    if (this.ticketType === TicketType.Contracted) {
      data.counterpartSelicAccount = counterpartModel.selicAccount;
    }

    if (this.ticketType === TicketType.CurrencyTerm) {
      data.counterpart.name = counterpartModel.name;
      data.counterpart.document = this.validator.sanitize(
        counterpartModel.document
      );
      data.counterpart.clearingAccount = counterpartModel.clearingAccount;
    }

    if (this.ticketType === TicketType.SwapCetip) {
      data.counterpartCetipAccount = counterpartModel.clearingAccount;
    }

    return data;
  }

  public fillComponentFieldsLoad<T>(model: any) {
    //carregando dados da model nos campos do component
    let data: any;
    data = Object.assign({}, model);

    try {
      let document =
        this.counterpartModel.type == 1
          ? this.validator.formatPhysicalDocument(data.counterpartDocument)
          : this.validator.formatDocument(data.counterpartDocument);

      if (this.ticketType != TicketType.CurrencyTerm) {
        this.counterpartModel.name = this.selectedCounterpart.name =
          data.counterpartName;
        this.counterpartModel.document = this.selectedCounterpart.document =
          document;
        this.counterpartModel.isNewCounterpart =
          this.selectedCounterpart.isNewCounterpart = data.isNewCounterpart;
      }

      if (this.ticketType === TicketType.Quota) {
        this.selectedCounterpartAccount.bank = data.fundBank;
        this.selectedCounterpartAccount.branch = data.fundBranch;
        this.selectedCounterpartAccount.account = data.fundAccount;
        this.bankService.sendBank(this.selectedCounterpartAccount.bank);
      }

      if (this.ticketType === TicketType.CETIP) {
        this.counterpartModel.type = this.selectedCounterpart.type =
          data.counterpartType;
        this.selectedCounterpartAccount.bank = data.counterpartBank;
        this.selectedCounterpartAccount.branch = data.counterpartBranch;
        this.selectedCounterpartAccount.account = data.counterpartAccount;
        this.bankService.sendBank(this.selectedCounterpartAccount.bank);
      }

      if (this.ticketType === TicketType.SELIC) {
        this.selectedCounterpartAccount.bank = data.fundBank;
        this.selectedCounterpartAccount.branch = data.fundBranch;
        this.selectedCounterpartAccount.account = data.fundAccount;
        this.counterpartModel.selicAccount =
          this.selectedCounterpart.selicAccount = data.counterpartSelicAccount;
        this.bankService.sendBank(this.selectedCounterpartAccount.bank);
      }

      if (this.ticketType === TicketType.Margin) {
        if (!this.isMoneyMargin) {
          this.counterpartModel.clearingAccount =
            this.selectedCounterpart.clearingAccount =
              data.counterpartClearingAccount;
        } else {
          this.selectedCounterpartAccount.bank = data.fundBank;
          this.selectedCounterpartAccount.branch = data.fundBranch;
          this.selectedCounterpartAccount.account = data.fundAccount;
          this.bankService.sendBank(this.selectedCounterpartAccount.bank);
        }

        if (this.isVariableIncomeMargin) {
          this.counterpartModel.brokerAccount =
            this.selectedCounterpart.brokerAccount =
              data.counterpartBrokerAccount;
        }
      }

      if (this.ticketType === TicketType.Contracted) {
        this.counterpartModel.selicAccount =
          this.selectedCounterpart.selicAccount = data.counterpartSelicAccount;
      }

      if (this.ticketType === TicketType.CurrencyTerm) {
        this.counterpartModel.name = this.selectedCounterpart.name =
          data.counterpart.name;
        this.counterpartModel.document = this.selectedCounterpart.document =
          data.counterpart.document;
        this.counterpartModel.clearingAccount =
          this.selectedCounterpart.clearingAccount =
            data.counterpart.clearingAccount;
      }

      if (this.ticketType === TicketType.SwapCetip) {
        this.counterpartModel.clearingAccount =
          this.selectedCounterpart.clearingAccount =
            data.counterpartCetipAccount;
      }

      if (this.model.counterpartDocument) {
        if (this.validator.validateDocument(this.model.counterpartDocument)) {
          this.counterpartModel.type = this.selectedCounterpart.type = 2;
        } else if (
          this.validator.validatePhysicalDocument(
            this.model.counterpartDocument
          )
        ) {
          this.counterpartModel.type = this.selectedCounterpart.type = 1;
        }
      }
    } catch (error) {
      return throwError(error);
    }
  }

  coalesce(value: any) {
    return value || "";
  }

  resetForm() {
    this.selectedCounterpart = new Counterpart();
    this.selectedCounterpartAccount = new CounterpartAccount();
    this.counterpartAccountList = null;
    this.counterpartForm.resetForm();

    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    this.formEnabled = enabled;
    setTimeout(() => {
      if (enabled) {
        Object.values(this.counterpartForm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit) {
        Object.values(this.counterpartForm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.counterpartModel[prop]);
  }

  validators = {
    document: (value) => 
      this.isSecondaryMarketCetip ||
      this.isSecondaryMarketQuota ||
      this.isNoFinancialCETIP ||
      this.isNoFinancialQuota ||
      this.isStructuredOrder ?
      (this.coalesce(value).length > 0 ? this.validateDocument(value) : false) : true,
    name: (value) =>
      this.isSecondaryMarketCetip ||
      this.isSecondaryMarketQuota ||
      this.isNoFinancialCETIP ||
      this.isNoFinancialQuota ||
      this.isStructuredOrder
        ? this.coalesce(value).length > 0
        : true,
    bank: () =>
      this.isSecondaryMarketCetip || this.isSecondaryMarketQuota || (this.isStructuredOrder && !this.isStructuredOrderNoFinancial)
        ? this.selectedCounterpartAccount.bank != null
        : true,
    branch: () =>
      this.isSecondaryMarketCetip || this.isSecondaryMarketQuota || (this.isStructuredOrder && !this.isStructuredOrderNoFinancial)
        ? this.selectedCounterpartAccount.branch != null
        : true,
    account: () =>
      this.isSecondaryMarketCetip || this.isSecondaryMarketQuota || (this.isStructuredOrder && !this.isStructuredOrderNoFinancial)
        ? this.selectedCounterpartAccount.account != null
        : true,
  };

  validateDocument(str: string): boolean {
    if (this.counterpartModel.type == PersonType.PhysicalPerson) {
      return this.validator.validatePhysicalDocument(str);
    } else {
      return this.validator.validateDocument(str);
    }
  }
}
